import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Store, Bell, LogOut, Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { AdminSidebarNav } from './AdminSidebarNav';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-gray-950 border-r border-gray-800 transition-[width] duration-300 flex flex-col sticky top-0 h-screen`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
          {sidebarOpen && (
            <img src="/logo-zonalibre.png" alt="Zona Libre" className="h-8" />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white hover:bg-gray-700"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        <AdminSidebarNav expanded={sidebarOpen} onExpand={() => setSidebarOpen(true)} />

        {/* User section */}
        <div className="p-4 border-t border-gray-700">
          <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
            <img
              src={adminUser.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin'}
              alt="Admin"
              className="w-10 h-10 rounded-full bg-gray-600"
            />
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{adminUser.name || 'Admin'}</p>
                <p className="text-xs text-gray-400 truncate">{adminUser.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-6">
          {/* Left side - Page title */}
          <h1 className="text-lg font-semibold text-white">
            Panel de Administración
          </h1>

          {/* Right side - Actions */}
          <div className="flex items-center gap-4">
            {/* Go to store */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Ver tienda"
            >
              <Store className="w-5 h-5" />
              <span className="text-sm hidden md:inline">Ver tienda</span>
            </a>

            {/* Search */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white hover:bg-gray-700"
              title="Buscar"
            >
              <Search className="w-5 h-5" />
            </Button>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-700 relative"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                title="Notificaciones"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                  3
                </span>
              </Button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                  <div className="p-4 border-b border-gray-700">
                    <h3 className="text-sm font-semibold text-white">Notificaciones</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <div className="p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-700">
                      <p className="text-sm text-white">Nueva orden recibida</p>
                      <p className="text-xs text-gray-400 mt-1">Hace 5 minutos</p>
                    </div>
                    <div className="p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-700">
                      <p className="text-sm text-white">Nuevo usuario registrado</p>
                      <p className="text-xs text-gray-400 mt-1">Hace 1 hora</p>
                    </div>
                    <div className="p-4 hover:bg-gray-700 cursor-pointer">
                      <p className="text-sm text-white">Producto sin stock</p>
                      <p className="text-xs text-gray-400 mt-1">Hace 2 horas</p>
                    </div>
                  </div>
                  <div className="p-3 border-t border-gray-700 text-center">
                    <Button variant="ghost" size="sm" className="text-orange-500 hover:text-orange-400 text-sm">
                      Ver todas
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-400 hover:text-red-400 hover:bg-gray-700"
              title="Cerrar sesión"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto bg-gray-900">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
